/**
 * @fileoverview Present document-specific commands for a workspace-owned selection. Clipboard and action callbacks retain canonical target identity; `documentSources.js` supplies internal links and download sources (the latter through the re-export in `DocumentExplorerPreviewColumn.js`). The parent owns mutation execution and capabilities, while this menu owns visibility and dismissal.
 */

import React, { useContext, useEffect, useRef } from 'react';
import AppIcon from '../shared/AppIcon';
import ToastContext from '../../ToastContext';
import { documentInternalUrl } from './documentSources';
import { documentDownloadSource } from './DocumentExplorerPreviewColumn';
import './FileContextMenu.css';

const MENU_WIDTH = 208;
const MENU_HEIGHT = 402;
const VIEWPORT_GUTTER = 8;



export function isFileContextMenuKey(event) {
  return event.key === 'ContextMenu' || (event.key === 'F10' && event.shiftKey);
}



export function fileContextMenuState(event, document) {
  event.preventDefault();
  event.stopPropagation();
  const bounds = event.currentTarget?.getBoundingClientRect?.();
  const keyboardTriggered = event.type === 'keydown';
  const requestedX = keyboardTriggered || !event.clientX
    ? (bounds?.left || VIEWPORT_GUTTER) + 24
    : event.clientX;
  const requestedY = keyboardTriggered || !event.clientY
    ? (bounds?.top || VIEWPORT_GUTTER) + 28
    : event.clientY;
  return {
    document,
    focusItem: keyboardTriggered,
    x: Math.max(VIEWPORT_GUTTER, Math.min(requestedX, window.innerWidth - MENU_WIDTH - VIEWPORT_GUTTER)),
    y: Math.max(VIEWPORT_GUTTER, Math.min(requestedY, window.innerHeight - MENU_HEIGHT - VIEWPORT_GUTTER)),
  };
}



function FileContextMenu({ menu, onClose, onMove, onCopy, onCopyToClipboard, onCut, onDuplicate, onRename, onTrash }) {
  const moveRef = useRef(null);
  const showToast = useContext(ToastContext);

  useEffect(

  () => {
    if (!menu) return undefined;
    const close =

    () => onClose();
    const handleKeyDown =

    (event) => {
      if (event.key === 'Escape') close();
    };
    window.addEventListener('pointerdown', close);
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', close);
    window.addEventListener('scroll', close, true);
    return   () => {
      window.removeEventListener('pointerdown', close);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [menu, onClose]);

  useEffect(() => {
    if (menu?.focusItem) moveRef.current?.focus();
  }, [menu]);

  if (!menu?.document) return null;
  const filename = menu.document.filename || 'Untitled document';
  const canMove = menu.document.available_locally !== false && menu.document.capabilities?.can_move !== false;
  const canDownload = menu.document.available_locally !== false &&
    menu.document.capabilities?.can_download !== false;
  const canTrash = menu.document.available_locally !== false &&
    menu.document.capabilities?.can_delete !== false;
  const downloadSource = canDownload ? documentDownloadSource(menu.document) : '';
  const directUrl = documentInternalUrl(menu.document);
  const copyLink =

  async () => {
    onClose();
    try {
      await navigator.clipboard.writeText(directUrl);
      showToast?.({ severity: 'success', summary: 'Link copied', detail: filename });
    } catch (_error) {
      showToast?.({ severity: 'info', summary: 'Document link', detail: directUrl });
    }
  };
  return (
    <div
      className="documents-file-context-menu"
      role="menu"
      aria-label={`${filename} actions`}
      style={{ left: menu.x, top: menu.y }}
      onContextMenu={(event) => event.preventDefault()}
      onPointerDown={(event) => event.stopPropagation()}>
      <button
        ref={moveRef}
        type="button"
        role="menuitem"
        disabled={!canMove}
        onClick={() => {
          onClose();
          onMove(menu.document);
        }}>
        <AppIcon icon="pi pi-folder" aria-hidden="true" />
        {canMove ? 'Move file' : 'Import before moving'}
      </button>
      {downloadSource ? (
        <a
          role="menuitem"
          href={downloadSource}
          download={filename}
          onClick={onClose}>
          <AppIcon icon="pi pi-download" aria-hidden="true" /> Download
        </a>
      ) : (
        <button type="button" role="menuitem" disabled title="The original file is not available to download">
          <AppIcon icon="pi pi-download" aria-hidden="true" /> Download
        </button>
      )}
      {menu.document.capabilities?.can_copy !== false && onCopy && (
        <button type="button" role="menuitem" onClick={() => { onClose(); onCopy(menu.document); }}>
          <AppIcon icon="pi pi-copy" aria-hidden="true" /> Copy to…
        </button>
      )}
      {menu.document.capabilities?.can_copy !== false && onCopyToClipboard && (
        <button type="button" role="menuitem" onClick={() => { onClose(); onCopyToClipboard(menu.document); }}>
          <AppIcon icon="pi pi-copy" aria-hidden="true" /> Copy
        </button>
      )}
      {menu.document.capabilities?.can_move !== false && onCut && (
        <button type="button" role="menuitem" onClick={() => { onClose(); onCut(menu.document); }}>
          <AppIcon icon="pi pi-scissors" aria-hidden="true" /> Cut
        </button>
      )}
      {directUrl && (
        <button type="button" role="menuitem" onClick={copyLink}>
          <AppIcon icon="pi pi-link" aria-hidden="true" /> Copy link
        </button>
      )}
      {menu.document.capabilities?.can_copy !== false && onDuplicate && (
        <button type="button" role="menuitem" onClick={() => { onClose(); onDuplicate(menu.document); }}>
          <AppIcon icon="pi pi-clone" aria-hidden="true" /> Duplicate
        </button>
      )}
      {menu.document.capabilities?.can_rename !== false && onRename && (
        <button type="button" role="menuitem" onClick={() => { onClose(); onRename(menu.document); }}>
          <AppIcon icon="pi pi-pencil" aria-hidden="true" /> Rename
        </button>
      )}
      {onTrash && (
        <button
          type="button"
          role="menuitem"
          className="is-danger"
          disabled={!canTrash}
          onClick={() => { onClose(); onTrash(menu.document); }}>
          <AppIcon icon="pi pi-trash" aria-hidden="true" /> Move to trash
        </button>
      )}
    </div>
  );
}

export default FileContextMenu;
